import React, { useState, useEffect, Suspense } from 'react';
import * as styles from './videoHighlight.module.css';
import { graphql } from 'gatsby';
import {
    KontentMedia,
    BrandColor,
    KontentStringCodenameValue,
    CTAConfiguration,
} from '../../../types/KontentProps';
import Modal from 'react-modal';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import CloseButton from '../Global/CloseButton';
import ReactPlayer from 'react-player';

interface VideoHighlightProps extends CTAConfiguration {
    anchor_to_lightbox?: KontentStringCodenameValue;
    background_color?: BrandColor;
    component_anchor?: { value: string };
    description?: { value: string };
    font_color?: BrandColor;
    headline?: { value: string };
    play_icon_color?: BrandColor;
    play_icon_background_color?: BrandColor;
    video_location?: KontentStringCodenameValue;
    video_thumbnail?: KontentMedia;
    video_play_icon: KontentMedia;
    video_url?: { value: string };
    cta_font_color?: BrandColor;
}

const VideoHighlight: React.FC<VideoHighlightProps> = ({
    anchor_to_lightbox,
    background_color,
    component_anchor,
    description,
    font_color,
    headline,
    play_icon_color,
    play_icon_background_color,
    video_location,
    video_thumbnail,
    video_play_icon,
    video_url,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_window_action,
    cta_configuration__cta_location_anchor_point,
    cta_font_color,
    ...props
}) => {
    const [modalIsOpen, setIsOpen] = useState(false);
    const [autoplayVideo, setAutoplay] = useState(true);
    const [anchor, setAnchor] = useState('');
    const [isPlayingInEmbed, setIsPlayingInEmbed] = useState(false);
    const componentAnchorValue = component_anchor?.value || '';

    const headlineText = headline?.value || '';
    const descriptionText = description?.value || '';
    const videoUrl = video_url?.value || '';
    const isEmbed = video_location?.value?.[0]?.codename === 'embedded';
    const anchorToLightbox =
        anchor_to_lightbox?.value?.[0]?.codename === 'enable';

    const backgroundColor = background_color?.value?.[0]?.elements
        .color_hex_value.value
        ? '#' + background_color.value[0].elements.color_hex_value.value
        : '';
    const fontColor = font_color?.value?.[0]?.elements.color_hex_value.value
        ? '#' + font_color.value[0].elements.color_hex_value.value
        : '';
    const playIconColor = play_icon_color?.value?.[0]?.elements.color_hex_value
        .value
        ? '#' + play_icon_color.value[0].elements.color_hex_value.value
        : '';
    const playIconBackgroundColor = play_icon_background_color?.value?.[0]
        ?.elements.color_hex_value.value
        ? '#' +
          play_icon_background_color.value[0].elements.color_hex_value.value
        : '';
    const ctaColor = cta_font_color?.value?.[0]?.elements.color_hex_value
        ? '#' + cta_font_color.value[0].elements.color_hex_value.value
        : '';

    const videoThumbnailUrl =
        video_thumbnail?.value?.[0]?.elements.file.value?.[0]?.url || '';

    const videoThumbnailAlt =
        video_thumbnail?.value?.[0]?.elements.file.value?.[0]?.description ||
        '';

    // Play icon SVG/image
    const playIconUrl =
        video_play_icon?.value?.[0]?.elements?.file?.value?.[0]?.url;

    // CTA
    const ctaLabel = cta_configuration__cta_label?.value;
    const ctaTarget =
        cta_configuration__cta_window_action?.value?.[0]?.codename ===
        'new_window'
            ? '_blank'
            : undefined;
    const ctaHref = cta_configuration__cta_location_anchor_point?.value
        ? BuildLinkHref(cta_configuration__cta_location) +
          '/#' +
          cta_configuration__cta_location_anchor_point?.value
        : BuildLinkHref(cta_configuration__cta_location);

    // Anchor-to-lightbox logic
    useEffect(() => {
        if (
            typeof window !== 'undefined' &&
            anchorToLightbox &&
            componentAnchorValue == window.location.hash
        ) {
            setAnchor(window.location.hash.replace('#', ''));
        }
    }, [anchorToLightbox]);

    useEffect(() => {
        if (
            anchorToLightbox &&
            anchor &&
            videoUrl &&
            componentAnchorValue === anchor
        ) {
            setAutoplay(false);
            setIsOpen(true);
        }
    }, [anchor, anchorToLightbox, videoUrl, componentAnchorValue]);

    const handleCloseBtnClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setIsOpen(false);
    };

    return (
        <div
            className={styles.videoHighlightContainer}
            id={componentAnchorValue || undefined}
            style={{ backgroundColor }}
        >
            {/* Left: Thumbnail with Play Icon */}
            <div className={styles.videoHighlightLeft}>
                {isEmbed && isPlayingInEmbed ? (
                    <Suspense fallback={<div>Loading video...</div>}>
                        <ReactPlayer
                            className={styles.inlineReactPlayer}
                            controls={true}
                            playing={true}
                            url={videoUrl}
                            style={{ background: '#000' }}
                        />
                    </Suspense>
                ) : (
                    <div className={styles.videoHighlightThumbnail}>
                        {videoThumbnailUrl && (
                            <img
                                src={videoThumbnailUrl}
                                alt={videoThumbnailAlt}
                                className={styles.thumbnailImage}
                            />
                        )}
                        {playIconUrl && (
                            <button
                                className={styles.videoHighlightPlayButton}
                                style={{
                                    backgroundColor: playIconBackgroundColor,
                                }}
                                aria-label="Play video"
                                onClick={() => {
                                    if (isEmbed) {
                                        setIsPlayingInEmbed(true);
                                    } else {
                                        setIsOpen(true);
                                        setAutoplay(true);
                                    }
                                }}
                            >
                                <img
                                    src={playIconUrl}
                                    alt="Play"
                                    className={styles.videoHighlightPlayIcon}
                                    style={{
                                        filter: playIconColor
                                            ? `drop-shadow(0 0 2px ${playIconColor})`
                                            : undefined,
                                    }}
                                />
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div
                className={styles.videoHighlightRight}
                style={{ color: fontColor }}
            >
                {headline && (
                    <div className={styles.headline}>
                        <h3>{headlineText}</h3>
                    </div>
                )}
                {description && (
                    <div className={styles.paragraph}>{descriptionText}</div>
                )}
                {ctaLabel && (
                    <div className={styles.ctaContainer}>
                        <a
                            href={ctaHref}
                            className={styles.ctaPlayLink}
                            style={{
                                color: ctaColor,
                            }}
                            target={ctaTarget}
                        >
                            {ctaLabel}
                            <span className={styles.ctaArrow}>&nbsp; &gt;</span>
                        </a>
                    </div>
                )}
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={() => setIsOpen(false)}
                shouldCloseOnOverlayClick={true}
                overlayClassName={styles.videoHighlightModalOverlay}
                style={{
                    content: {
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        inset: 'unset', // Let the overlay center it
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'visible',
                    },
                }}
            >
                <div className={styles.videoHighlightModalContent}>
                    <div className={styles.closeButton}>
                        <CloseButton
                            visualStyle="orange"
                            onClick={handleCloseBtnClick}
                        />
                    </div>
                    <Suspense fallback={<div>Loading video...</div>}>
                        <ReactPlayer
                            controls={true}
                            playing={autoplayVideo}
                            width="80vw"
                            height="56vw"
                            url={videoUrl}
                            style={{ maxWidth: 1024, maxHeight: 600 }}
                        />
                    </Suspense>
                </div>
            </Modal>
        </div>
    );
};

export default VideoHighlight;

// ...GraphQL fragment remains unchanged...
export const fragmentVideoHighlight = graphql`
    fragment VideoHighlight on kontent_item_component___video_highlight {
        elements {
            anchor_to_lightbox {
                value {
                    name
                    codename
                }
            }
            background_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            component_anchor {
                value
            }
            description {
                value
            }
            font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            headline {
                value
            }
            play_icon_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            play_icon_background_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_name {
                                value
                            }
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            video_location {
                value {
                    codename
                    name
                }
            }
            video_thumbnail {
                value {
                    ... on kontent_item_media___image {
                        id
                        elements {
                            file {
                                value {
                                    url
                                }
                            }
                        }
                    }
                }
            }
            video_url {
                value
            }
            video_play_icon {
                ...kontentImage
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    name
                }
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_label {
                value
            }
            cta_font_color {
                ...kontentColor
            }
        }
    }
`;
